import { IdentityApi } from '@backstage/core-plugin-api';
import { GovernanceApi } from '../api';
import {
  activeGovernanceEntityStatuses,
  PolicyViolationOutcome,
  PolicyViolationsCountMap,
} from '@agilelab/plugin-wb-governance-common';
import { batchPromiseAll } from '@agilelab/plugin-wb-platform-common';

type DataContractEvaluationIdentifier = {
  policyId: string;
  externalId: string;
  environment: string;
};

/**
 * Given an array of resources, computes their policy violations count in parallel requests (maxItemsPerRequest elements per request) and merges them into a single map (Used to prevent sending payloads too big when requesting data for a lot of items)
 */
export async function batchComputePolicyViolationsCount(
  resources: Record<string, string[]>,
  governanceApi: GovernanceApi,
  identityApi: IdentityApi,
  environment: string,
  maxItemsPerRequest = 100,
  signal?: AbortSignal,
) {
  const chunks: string[][] = [];

  const identifiers: string[] = [];
  for (const [key, ids] of Object.entries(resources)) {
    identifiers.push(key);
    identifiers.push(...ids);
  }

  identifiers.forEach(id => {
    let last = chunks.at(-1);
    if (!last || last.length >= maxItemsPerRequest) {
      last = [];
      chunks.push(last);
    }
    last.push(id);
  });

  const token = (await identityApi.getCredentials()).token;

  const counts = await batchPromiseAll(
    chunks.map(
      c => () =>
        governanceApi.computePolicyViolationsCount(
          {
            environment: environment,
            // use a set to make sure each id is distinct
            resources: Array.from(new Set(c)),
          },
          { token },
        ),
    ),
    { batchSize: 5, signal },
  );

  const merged: PolicyViolationsCountMap = counts.reduce((prev, curr) => {
    for (const [key, val] of curr.entries()) {
      prev.set(key, val);
    }
    return prev;
  }, new Map());

  const parentCounts: PolicyViolationsCountMap = new Map();

  for (const [parent, children] of Object.entries(resources)) {
    const parentMap = new Map<PolicyViolationOutcome, number>();

    const mergedParent = merged.get(parent);
    if (mergedParent) {
      for (const [outcome, count] of mergedParent.entries()) {
        parentMap.set(outcome, count);
      }
    }

    for (const child of children) {
      const mergedChild = merged.get(child);
      if (mergedChild) {
        for (const [outcome, count] of mergedChild.entries()) {
          const prev = parentMap.get(outcome) ?? 0;
          parentMap.set(outcome, prev + count);
        }
      }
    }

    parentCounts.set(parent, parentMap);
  }
  return parentCounts;
}

export async function getPoliciesNamesByIds(
  ids: string[],
  governanceApi: GovernanceApi,
  identityApi: IdentityApi,
): Promise<Map<string, string>> {
  const token = (await identityApi.getCredentials()).token;

  const result = await governanceApi.getPolicies(
    { 'id-in': ids.join(',') },
    { token },
  );

  return result.data.reduce((map: Map<string, string>, policy: any) => {
    map.set(policy.id, policy.name);
    return map;
  }, new Map());
}

/**
 * Given an array containing for each element the metadata required to fetch the evaluation status of a data contract,
 * fetches them in parallel requests (maxItemsPerRequest elements per request) and merges them into a single array. (Used to prevent sending payloads too big when requesting data for a lot of items)
 */
export async function batchGetDataContractsEvaluationStatus(
  identifiers: DataContractEvaluationIdentifier[],
  governanceApi: GovernanceApi,
  identityApi: IdentityApi,
  maxItemsPerRequest = 100,
) {
  const chunks: DataContractEvaluationIdentifier[][] = [];

  identifiers.forEach(id => {
    let last = chunks.at(-1);
    if (!last || last.length >= maxItemsPerRequest) {
      last = [];
      chunks.push(last);
    }
    last.push(id);
  });

  const token = (await identityApi.getCredentials()).token;

  const evaluations = await Promise.all(
    chunks.map(c =>
      governanceApi.findResourceEvaluationStatus(
        {
          filters: {
            governanceEntityFilter: {
              statusIn: activeGovernanceEntityStatuses,
            },
            statusFilter: {
              identifierIn: c.map(d => ({
                governanceEntityId: d.policyId,
                resourceIdentifier: {
                  resourceExternalId: d.externalId,
                  environment: d.environment,
                },
              })),
            },
          },
          resultOptions: {},
        },
        { token },
      ),
    ),
  );

  return evaluations.flatMap(e => e.data);
}
